import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { HERO_IMAGE } from "@/data/artworks";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { delay, duration: 1.1, ease: [0.22, 1, 0.36, 1] },
});

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.5, 2.5]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col overflow-hidden pt-60"
      style={{ perspective: 1200 }}
    >
      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center px-6 text-center">
        <div className="overflow-hidden">
          <motion.p {...lineAnim(0.35)} className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#9A7B24] sm:text-xs">
            Private Visual Archive — Est. MMXXVI
          </motion.p>
        </div>
        <h1 className="font-display mt-6 font-light leading-[0.95] tracking-tight text-[#131311]">
          <span className="block overflow-hidden">
            <motion.span {...lineAnim(0.5)} className="block whitespace-nowrap text-4xl sm:text-6xl lg:text-7xl">
              ROY A SMITH
            </motion.span>
          </span>
        </h1>
        <div className="mx-auto mt-10 flex w-fit flex-col items-start gap-1">
          {["Serial", "Entrepreneurial", "Disruptive", "Innovator"].map((word, i) => (
            <span key={word} className="block overflow-hidden">
              <motion.span {...lineAnim(0.85 + i * 0.15)} className="flex items-baseline">
                <span className="inline-block w-[4.5rem] text-center font-display text-[4.5rem] font-light leading-none text-[#B08D2E] sm:w-[5.5rem] sm:text-[5.5rem]">
                  {word[0]}
                </span>
                <span className="ml-2 font-mono text-xs uppercase tracking-[0.35em] text-zinc-500 sm:text-sm">
                  {word.slice(1)}
                </span>
              </motion.span>
            </span>
          ))}
        </div>
      </motion.div>

      <motion.div
        style={{ rotateX, rotateY }}
        initial={{ opacity: 0, y: 70 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="mx-6 mb-20 mt-12 flex-1 lg:mx-16"
      >
        <div className="h-[52vh] overflow-hidden border border-black/10 shadow-[0_30px_60px_-30px_rgba(19,19,17,0.35)]">
          <motion.img
            src={HERO_IMAGE}
            alt="Roy A Smith"
            style={{ y: imgY, objectPosition: "center 22%" }}
            className="h-[116%] w-full scale-[1.08] object-cover"
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/90 drop-shadow">Scroll</span>
        <div className="h-12 w-px overflow-hidden bg-white/30">
          <motion.div
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-full w-full bg-[#D4AF37]"
          />
        </div>
      </motion.div>
    </section>
  );
};
